/**
 * Gemini Onboarding Contacts endpoints (backend OR-1312).
 *
 *   Global pool (deduped by email, shared across submissions):
 *     GET    /v2/contacts/?search=&limit=   → substring match on email/name
 *     POST   /v2/contacts/                  → create a pool contact
 *
 *   Per submission (attach model — the pool row is the SoT):
 *     GET    /v2/submissions/:id/onboarding-contacts/
 *     POST   /v2/submissions/:id/onboarding-contacts/   body: { contact_id }
 *     DELETE /v2/submissions/:id/onboarding-contacts/:contactId
 *
 * Attaching an existing contact never duplicates it; creating a brand
 * new one is a two-step create-then-attach. The backend syncs attached
 * contacts to HubSpot in the background.
 */
import type { OnboardingContact, OnboardingContactCreatePayload, OnboardingContactWire } from "../../types/onboarding-contact";
import { wireToOnboardingContact } from "../../types/onboarding-contact";
import { getJsonReal, sendJsonReal } from "../client";

function onboardingBase(submissionId: string): string {
    // Collection path keeps the trailing slash (FastAPI 307 otherwise).
    return `/v2/submissions/${encodeURIComponent(submissionId)}/onboarding-contacts`;
}

/** Search the global pool — feeds the "Select Email" dropdowns. */
export function searchContactPool(term: string, limit = 20): Promise<OnboardingContact[]> {
    const q = term.trim();
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    params.set("limit", String(limit));
    return getJsonReal<OnboardingContactWire[]>(`/v2/contacts/?${params}`).then((rows) => rows.map(wireToOnboardingContact));
}

/** Create a brand-new pool contact (email is the dedup key). */
export function createPoolContact(payload: OnboardingContactCreatePayload): Promise<OnboardingContact> {
    return sendJsonReal<OnboardingContactWire>("/v2/contacts/", "POST", payload).then(wireToOnboardingContact);
}

export function listOnboardingContacts(submissionId: string): Promise<OnboardingContact[]> {
    return getJsonReal<OnboardingContactWire[]>(`${onboardingBase(submissionId)}/`).then((rows) => rows.map(wireToOnboardingContact));
}

export function attachOnboardingContact(submissionId: string, contactId: string): Promise<OnboardingContact> {
    return sendJsonReal<OnboardingContactWire>(`${onboardingBase(submissionId)}/`, "POST", { contact_id: contactId }).then(
        wireToOnboardingContact,
    );
}

export function detachOnboardingContact(submissionId: string, contactId: string): Promise<void> {
    return sendJsonReal<void>(`${onboardingBase(submissionId)}/${encodeURIComponent(contactId)}`, "DELETE");
}
